import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import WorkerSignupLayout, {
  NAVY, AMBER, WHITE, FONT_HEAD, FONT_BODY, StepPills,
} from './WorkerSignupLayout';
import ImageCropModal from '../../components/ImageCropModal';
import LineIcon from '../../components/LineIcon';

// Stable English values stored in worker_profiles.veteran_status regardless of UI
// language -- only the rendered option label is translated (see WorkerStep3's TRADES
// for the same pattern, where translating the raw value would break downstream matching).
const VETERAN_OPTIONS = [
  { value: 'Not a veteran',                     key: 'notVeteran' },
  { value: 'Veteran — honorably discharged',    key: 'honorablyDischarged' },
  { value: 'Active duty',                       key: 'activeDuty' },
  { value: 'Veteran — other discharge',         key: 'otherDischarge' },
];

const inp = {
  width: '100%', padding: '11px 13px', fontSize: '0.95rem',
  border: '1.5px solid #E2E6EB', borderRadius: '8px',
  color: NAVY, outline: 'none', backgroundColor: WHITE,
};
const sel = { ...inp, cursor: 'pointer' };
const lbl = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '5px' };
const fld = { marginBottom: '1.1rem' };
const g2  = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.1rem' };
const card = {
  backgroundColor: WHITE, borderRadius: '16px',
  boxShadow: '0 2px 24px rgba(0,0,0,0.07)',
  padding: '2rem', marginBottom: '1.25rem',
};

export default function WorkerStep2() {
  const navigate  = useNavigate();
  const { t } = useTranslation();
  const photoRef  = useRef(null);
  const photoUploadPromiseRef = useRef(null);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoMsg, setPhotoMsg] = useState('');
  const [cropFile, setCropFile] = useState(null);
  const [saveError, setSaveError] = useState('');
  const [form, setForm] = useState({
    veteranStatus: '',
    city: 'Chicago', state: 'IL',
    bio: '', giBill: false, secondChance: false,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  // Reload previously-saved answers so navigating back and then forward again
  // doesn't show blank fields even though this step's data already exists.
  useEffect(() => {
    const loadExisting = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;
      const { data: profile } = await supabase.from('worker_profiles')
        .select('veteran_status, city, state, bio, gi_bill_eligible, second_chance, profile_photo_url')
        .eq('id', userId).maybeSingle();
      if (profile) {
        setForm(p => ({
          ...p,
          veteranStatus: profile.veteran_status || '',
          city: profile.city || p.city,
          state: profile.state || p.state,
          bio: profile.bio || '',
          giBill: !!profile.gi_bill_eligible,
          secondChance: !!profile.second_chance,
        }));
        if (profile.profile_photo_url) setPhotoUrl(profile.profile_photo_url);
      }
    };
    loadExisting();
  }, []);

  const handleContinue = async () => {
    const { data: { session } } = await supabase.auth.getSession()
    const userId = session?.user?.id
    if (!userId) { navigate('/worker/signup'); return }

    // A photo upload triggered by handlePhoto may still be in flight (upload +
    // DB save are both async) -- wait for it so clicking Continue right after
    // picking a photo can't navigate away before the photo is actually saved.
    if (photoUploadPromiseRef.current) {
      await photoUploadPromiseRef.current;
    }

    // Only advance once the saved row comes back -- a failed or RLS-filtered write would
    // otherwise carry the worker on to a dashboard with no profile.
    setSaveError('');
    const { data: saved, error } = await supabase.from('worker_profiles').upsert({
      id: userId,
      veteran_status: form.veteranStatus,
      city: form.city,
      state: form.state,
      bio: form.bio,
      gi_bill_eligible: form.giBill,
      second_chance: form.secondChance
    }, { onConflict: 'id' }).select('id')
    if (error || saved?.length !== 1) { setSaveError(t('worker.signup.saveFailed')); return }
    navigate('/worker/signup/trade')
  }

  // Selecting a file opens the crop step first; upload is deferred until the user
  // confirms the crop (handleCropConfirm below).
  const handlePhoto = e => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;
    setCropFile(file);
  };

  // Uploads the cropped image immediately (rather than deferring to Continue) so the
  // photo is safely saved before the user can navigate away -- matches the pattern
  // already used for this same upload in WorkerDashboard.jsx's My Profile tab. Uses
  // upsert (not update) since worker_profiles may not have a row yet this early in signup.
  const handleCropConfirm = (blob, outputType) => {
    setCropFile(null);
    const previewUrl = URL.createObjectURL(blob);
    setPhotoUrl(previewUrl);

    photoUploadPromiseRef.current = (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) return;

      setPhotoUploading(true);
      setPhotoMsg('');
      const path = `${userId}/avatar`;
      const { error: uploadError } = await supabase.storage
        .from('profile-photos')
        .upload(path, blob, { upsert: true, contentType: outputType });
      if (uploadError) {
        setPhotoMsg(t('worker.signup.step2.uploadFailed'));
        setPhotoUploading(false);
        return;
      }
      const { data: urlData } = supabase.storage.from('profile-photos').getPublicUrl(path);
      const publicUrl = `${urlData.publicUrl}?t=${Date.now()}`;
      const { data: savedPhoto, error: photoRowError } = await supabase.from('worker_profiles')
        .upsert({ id: userId, profile_photo_url: publicUrl }, { onConflict: 'id' }).select('id');
      if (photoRowError || savedPhoto?.length !== 1) setPhotoMsg(t('worker.signup.step2.uploadFailed'));
      setPhotoUploading(false);
    })();
  };

  return (
    <>
    {cropFile && (
      <ImageCropModal
        file={cropFile}
        onCancel={() => setCropFile(null)}
        onConfirm={handleCropConfirm}
      />
    )}
    <WorkerSignupLayout
      step={2}
      tipText={t('worker.signup.step2.tip')}
      onBack={() => navigate('/worker/signup')}
      onContinue={handleContinue}
    >
      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase', marginBottom: '1rem' }}>
        {t('worker.signup.step2.eyebrow')}
      </div>

      <StepPills current={2} />

      <div style={card}>
        <h1 style={{ fontFamily: FONT_HEAD, fontSize: '2.1rem', color: NAVY, letterSpacing: '0.03em', marginBottom: '0.35rem' }}>
          {t('worker.signup.step2.title')}
        </h1>
        <p style={{ fontSize: '0.9rem', color: 'rgba(13,27,42,0.52)', marginBottom: '1.75rem', lineHeight: 1.5 }}>
          {t('worker.signup.step2.subtitle')}
        </p>

        {/* Photo upload */}
        <div style={{ ...fld, display: 'flex', alignItems: 'flex-start', gap: '1.25rem' }}>
          <div>
            <div
              onClick={() => photoRef.current.click()}
              style={{
                width: '90px', height: '90px', borderRadius: '50%',
                border: `2px dashed ${photoUrl ? AMBER : '#D0D4DA'}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', overflow: 'hidden',
                backgroundColor: 'rgba(13,27,42,0.04)',
                backgroundImage: photoUrl ? `url(${photoUrl})` : 'none',
                backgroundSize: 'cover', backgroundPosition: 'center',
                flexShrink: 0,
              }}
            >
              {!photoUrl && (
                <div style={{ textAlign: 'center', color: 'rgba(13,27,42,0.35)' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                    <circle cx="12" cy="13" r="4" />
                  </svg>
                  <div style={{ fontSize: '0.65rem', marginTop: '3px', fontWeight: 600 }}>{t('worker.signup.step2.addPhoto')}</div>
                </div>
              )}
            </div>
            <input ref={photoRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handlePhoto} />
          </div>
          <div>
            <div style={{ fontSize: '0.82rem', fontWeight: 600, color: NAVY, marginBottom: '4px' }}>
              {t('worker.signup.step2.photoLabel')} <span style={{ fontWeight: 400, color: 'rgba(13,27,42,0.45)' }}>{t('worker.signup.step2.photoOptional')}</span>
            </div>
            <div style={{
              display: 'flex', alignItems: 'flex-start', gap: '8px',
              backgroundColor: 'rgba(245,166,35,0.1)', border: '1px solid rgba(245,166,35,0.3)',
              borderRadius: '8px', padding: '9px 12px',
              fontSize: '0.8rem', color: NAVY, lineHeight: 1.5,
            }}>
              <LineIcon name="camera" size={16} color="rgba(13,27,42,0.6)" style={{ marginTop: '2px' }} />
              {t('worker.signup.step2.photoHint')}
            </div>
            {photoUploading && (
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'rgba(13,27,42,0.5)', marginTop: '6px' }}>{t('worker.signup.step2.uploading')}</div>
            )}
            {photoMsg && (
              <div style={{ fontSize: '0.78rem', fontWeight: 600, color: '#DC2626', marginTop: '6px' }}>{photoMsg}</div>
            )}
          </div>
        </div>

        {/* Veteran status */}
        <div style={fld}>
          <label style={lbl}>{t('worker.signup.step2.veteranStatusLabel')}</label>
          <select style={sel} value={form.veteranStatus} onChange={e => set('veteranStatus', e.target.value)}>
            <option value="">{t('worker.signup.step2.veteranStatusPlaceholder')}</option>
            {VETERAN_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{t(`worker.signup.step2.veteranOptions.${opt.key}`)}</option>
            ))}
          </select>
        </div>

        {/* City + State */}
        <div style={g2}>
          <div>
            <label style={lbl}>{t('worker.signup.step2.cityLabel')}</label>
            <input style={inp} placeholder="Chicago" value={form.city} onChange={e => set('city', e.target.value)} />
          </div>
          <div>
            <label style={lbl}>{t('worker.signup.step2.stateLabel')}</label>
            <select style={sel} value={form.state} onChange={e => set('state', e.target.value)}>
              <option value="IL">Illinois</option>
              <option value="IN">Indiana</option>
              <option value="WI">Wisconsin</option>
              <option value="MI">Michigan</option>
              <option value="OH">Ohio</option>
              <option value="MO">Missouri</option>
              <option value="IA">Iowa</option>
              <option value="MN">Minnesota</option>
            </select>
          </div>
        </div>

        {/* Bio */}
        <div style={fld}>
          <label style={lbl}>{t('worker.signup.step2.bioLabel')}</label>
          <textarea
            style={{ ...inp, minHeight: '120px', resize: 'vertical', lineHeight: 1.6 }}
            placeholder={t('worker.signup.step2.bioPlaceholder')}
            value={form.bio}
            onChange={e => set('bio', e.target.value)}
          />
        </div>

        {/* GI Bill */}
        <div style={{ marginBottom: '0.85rem' }}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.875rem', color: 'rgba(13,27,42,0.75)', lineHeight: 1.5 }}>
            <input type="checkbox" checked={form.giBill} onChange={e => set('giBill', e.target.checked)}
              style={{ marginTop: '2px', accentColor: AMBER, width: '16px', height: '16px', flexShrink: 0 }} />
            {t('worker.signup.step2.giBillLabel')}
          </label>
        </div>

        {/* Second chance */}
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', cursor: 'pointer', fontSize: '0.875rem', color: 'rgba(13,27,42,0.75)', lineHeight: 1.5 }}>
          <input type="checkbox" checked={form.secondChance} onChange={e => set('secondChance', e.target.checked)}
            style={{ marginTop: '2px', accentColor: AMBER, width: '16px', height: '16px', flexShrink: 0 }} />
          {t('worker.signup.step2.secondChanceLabel')}
        </label>
      </div>
      {saveError && (
        <p role="alert" data-testid="signup-save-error" style={{ margin: '0 0 0.25rem', fontSize: '0.9rem', fontWeight: 600, color: '#B91C1C', lineHeight: 1.45, overflowWrap: 'anywhere' }}>
          {saveError}
        </p>
      )}
    </WorkerSignupLayout>
    </>
  );
}
